import { useMemo, useState } from 'react';
import type { DealEventTct } from '@dealstack/shared/lib/backendClient';
import type { RecordAny } from '@dealstack/shared/lib/common/common';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { DealEventLogTab } from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.types.ts';
import { getDealEventLogColumns } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogTable/DealEventLogTable.columns.tsx';
import { getDealEventLogQuery } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogQuery.utils.ts';
import { useDealEventFeed } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventFeed.ts';
import { useDealEventActors } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventActors.ts';
import { useDealEventSubjects } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventSubjects.ts';
import { useDealEventRecipients } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventRecipients.ts';
import { useDealEventSecurities } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventSecurities.ts';

export const useDealEventLogTable = (dealId: string, tab: DealEventLogTab) => {
	const tCapManage = useTranslationCapManage();
	const actorOptions = useDealEventActors(dealId, true);
	const recipients = useDealEventRecipients(dealId, tab === DealEventLogTab.MESSAGING);
	const subjects = useDealEventSubjects(dealId, tab === DealEventLogTab.TEAM_AND_ACCESS);
	const securityOptions = useDealEventSecurities(dealId, tab === DealEventLogTab.CAPITAL_EVENTS);

	const columns = useMemo(
		() =>
			getDealEventLogColumns(tab, tCapManage, {
				actorOptions,
				recipientOptions: recipients.options,
				recipientAsync: recipients.async,
				subjectOptions: subjects.options,
				subjectAsync: subjects.async,
				securityOptions,
			}),
		[
			tab,
			tCapManage,
			actorOptions,
			recipients.options,
			recipients.async,
			subjects.options,
			subjects.async,
			securityOptions,
		]
	);

	const { mantineTableDataOperations } = useMantineTableWithPaginationFilteringSorting<
		DealEventTct,
		RecordAny
	>(columns, {}, true);

	const { columnFilters, resetAll } = mantineTableDataOperations;

	// Filters restored from the url would otherwise apply behind a closed filter row.
	const [showColumnFilters, setShowColumnFilters] = useState(() => columnFilters.length > 0);

	const query = useMemo(() => getDealEventLogQuery(tab, columnFilters), [tab, columnFilters]);

	const feed = useDealEventFeed(dealId, query);

	return {
		columns,
		dataOperations: mantineTableDataOperations,
		showColumnFilters,
		setShowColumnFilters,
		resetFilters: resetAll,
		rows: feed.events,
		isLoading: feed.isLoading,
		isError: feed.isError,
		hasNextPage: feed.hasNextPage,
		isFetchingNextPage: feed.isFetchingNextPage,
		fetchNextPage: feed.fetchNextPage,
	};
};
